import { useState, useEffect, useCallback } from "react";
import { ProgramService } from "../services/programService";
import { TrainingWeek } from "../types/program";
import { logger } from "../utils/logger";

export interface TrainingDayWithWeek {
  id: number;
  day_of_the_week: number;
  is_rest_day: boolean;
  weekId: number;
  weekSequence: number;
  [key: string]: any;
}

interface UseTrainingWeeksOptions {
  programId?: number;
  trainingDaysPerWeek?: number;
  currentDayId?: number;
  currentWeekIndex?: number;
}

// Hook to manage training weeks and days for a program
export const useTrainingWeeks = ({
  programId,
  trainingDaysPerWeek,
  currentDayId,
  currentWeekIndex: initialWeekIndex = 0,
}: UseTrainingWeeksOptions) => {
  const [trainingWeeks, setTrainingWeeks] = useState<TrainingWeek[]>([]);
  const [allDays, setAllDays] = useState<TrainingDayWithWeek[]>([]);
  const [currentWeekIndex, setCurrentWeekIndex] =
    useState<number>(initialWeekIndex);
  const [currentDayIndex, setCurrentDayIndex] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTrainingWeeks = useCallback(async () => {
    if (!programId) {
      setTrainingWeeks([]);
      setAllDays([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const weeks = await ProgramService.getAllTrainingWeeks(programId);
      setTrainingWeeks(weeks);

      if (weeks.length === 0) {
        setError("No training weeks found for this program.");
        setAllDays([]);
        return;
      }

      const days: TrainingDayWithWeek[] = [];
      weeks.forEach((week) => {
        if (week.training_days && Array.isArray(week.training_days)) {
          const sortedWeekDays = [...week.training_days].sort(
            (a, b) => a.day_of_the_week - b.day_of_the_week
          );

          const displayedDays = sortedWeekDays.slice(
            0,
            trainingDaysPerWeek || 7
          );
          displayedDays.forEach((day) => {
            days.push({
              ...day,
              weekId: week.id,
              weekSequence: week.sequence_number,
            });
          });
        }
      });

      const sortedDays = days.sort((a, b) => {
        if (a.weekSequence !== b.weekSequence) {
          return a.weekSequence - b.weekSequence;
        }
        return a.day_of_the_week - b.day_of_the_week;
      });

      setAllDays(sortedDays);

      if (currentDayId) {
        const index = sortedDays.findIndex((day) => day.id === currentDayId);
        if (index !== -1) {
          setCurrentDayIndex(index);
        }
      }
    } catch (err: any) {
      logger.error("Error fetching training weeks:", err);
      setError(err.message || "Failed to load training weeks.");
      setTrainingWeeks([]);
      setAllDays([]);
    } finally {
      setLoading(false);
    }
  }, [programId, trainingDaysPerWeek, currentDayId]);

  useEffect(() => {
    if (programId) {
      fetchTrainingWeeks();
    }
  }, [fetchTrainingWeeks, programId]);

  // Week navigation helpers
  const getCurrentWeek = useCallback(() => {
    return trainingWeeks[currentWeekIndex] || null;
  }, [trainingWeeks, currentWeekIndex]);

  const getPreviousWeek = useCallback(() => {
    if (currentWeekIndex > 0 && trainingWeeks[currentWeekIndex - 1]) {
      return trainingWeeks[currentWeekIndex - 1];
    }
    return null;
  }, [currentWeekIndex, trainingWeeks]);

  const getNextWeek = useCallback(() => {
    if (
      currentWeekIndex < trainingWeeks.length - 1 &&
      trainingWeeks[currentWeekIndex + 1]
    ) {
      return trainingWeeks[currentWeekIndex + 1];
    }
    return null;
  }, [currentWeekIndex, trainingWeeks]);

  const goToPreviousWeek = useCallback(() => {
    if (currentWeekIndex > 0) {
      setCurrentWeekIndex(currentWeekIndex - 1);
    }
  }, [currentWeekIndex]);

  const goToNextWeek = useCallback(() => {
    if (currentWeekIndex < trainingWeeks.length - 1) {
      setCurrentWeekIndex(currentWeekIndex + 1);
    }
  }, [currentWeekIndex, trainingWeeks.length]);

  // Day navigation helpers
  const getPreviousDay = useCallback(() => {
    if (currentDayIndex > 0 && allDays[currentDayIndex - 1]) {
      return allDays[currentDayIndex - 1];
    }
    return null;
  }, [currentDayIndex, allDays]);

  const getNextDay = useCallback(() => {
    if (currentDayIndex < allDays.length - 1 && allDays[currentDayIndex + 1]) {
      return allDays[currentDayIndex + 1];
    }
    return null;
  }, [currentDayIndex, allDays]);

  const getCurrentDay = useCallback(() => {
    return allDays[currentDayIndex] || null;
  }, [currentDayIndex, allDays]);

  const getFilteredDaysForWeek = useCallback(
    (week: TrainingWeek) => {
      if (!week.training_days) return [];

      return [...week.training_days]
        .sort((a, b) => a.day_of_the_week - b.day_of_the_week)
        .slice(0, trainingDaysPerWeek || 7);
    },
    [trainingDaysPerWeek]
  );

  return {
    // Weeks data
    trainingWeeks,
    currentWeekIndex,
    setCurrentWeekIndex,
    totalWeeks: trainingWeeks.length,

    // Week navigation
    getCurrentWeek,
    getPreviousWeek,
    getNextWeek,
    goToPreviousWeek,
    goToNextWeek,

    // Days data
    allDays,
    currentDayIndex,
    totalDays: allDays.length,

    // Day navigation
    getPreviousDay,
    getNextDay,
    getCurrentDay,

    // Utilities
    getFilteredDaysForWeek,

    // State
    loading,
    error,
    refresh: fetchTrainingWeeks,
  };
};

export const useTrainingDays = useTrainingWeeks;
